import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { ExternalLink } from 'lucide-react'
import { useLocation, useParams } from 'router-v6'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'
import { useFlowStore } from '../store'

export interface ValidationError {
  stepTitle: string
  errors: string[]
  nodeId?: string
}

interface ValidationErrorPopoverProps {
  errors: ValidationError[]
  isOpen: boolean
  onClose: () => void
  trigger: React.ReactNode
  title?: string
}

export const ValidationErrorPopover = ({
  errors,
  isOpen,
  onClose,
  trigger,
  title,
}: ValidationErrorPopoverProps) => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useAppNavigate()
  const selectNode = useFlowStore((state) => state.selectNode)
  const unselectNode = useFlowStore((state) => state.unselectNode)
  const selectedNodeID = useFlowStore((state) => state.selectedNodeID)

  if (errors.length === 0) return trigger

  const isOnCanvasTab =
    !location.pathname.includes('/knowledge-base') &&
    !location.pathname.includes('/runs') &&
    !location.pathname.includes('/settings') &&
    !location.pathname.includes('/metrics')

  const handleOpenSidebar = (nodeId: string) => {
    const selectOrUnselectNode = () => {
      if (selectedNodeID !== nodeId) {
        selectNode(nodeId)
      } else {
        unselectNode()
      }
    }

    // Navigate to canvas tab if not already there
    if (!isOnCanvasTab && id) {
      navigate(`/agent/builder/${id}`, { replace: true })
      // Wait for canvas to render before selecting node
      setTimeout(selectOrUnselectNode, 1000)
    } else {
      selectOrUnselectNode()
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="bottom">
        <div className="p-4">
          {title && <h3 className="text-s-500 text-destructive mb-4">{title}</h3>}

          <div className="max-h-[200px] overflow-auto">
            <div className="space-y-4 [counter-reset:step-counter]">
              {errors.map((error, index) => (
                <div
                  key={error.nodeId || `error-${error.stepTitle}-${index}`}
                  className="space-y-2 [counter-increment:step-counter]"
                >
                  <div className="flex items-center justify-start">
                    <div className="text-s-600 text-secondary tracking-normal before:content-[counter(step-counter)'.'] before:mr-1">
                      {error.stepTitle}
                    </div>
                    {error.nodeId && (
                      <button
                        type="button"
                        onClick={() => error.nodeId && handleOpenSidebar(error.nodeId)}
                        className="ml-1 p-1 hover:bg-muted rounded-sm transition-colors"
                        title="Open node settings"
                        data-testid={`button-open-node-${error.nodeId}`}
                      >
                        <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </div>
                  <ul className="space-y-2 pl-2">
                    {error.errors.map((errorMessage) => (
                      <li
                        key={`${error.nodeId || error.stepTitle}-${errorMessage}`}
                        className="text-s-400 text-primary flex items-start"
                      >
                        <span className="inline-block w-1.5 h-1.5 bg-muted-foreground rounded-full mt-2 mr-3 flex-shrink-0" />
                        {errorMessage}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
